import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchGalleries,
  fetchGalleryById,
  createGallery,
  updateGallery,
  deleteGallery,
  archiveGallery,
  publishGallery,
  unpublishGallery,
  uploadGalleryImages,
  deleteGalleryImage,
  toggleImageVisibility,
  setCoverImage,
  updateGallerySettings,
  fetchGalleryOverallStats,
  clearErrorState,
  clearSuccessState,
  updateFilters,
  clearSelectedGallery,
} from "../../../app/slice/publicGallerySlice";

const usePublicGallery = () => {
  const dispatch = useDispatch();
  const {
    galleries,
    selectedGallery,
    stats,
    loading,
    error,
    success,
    filters,
  } = useSelector((state) => state.publicGallery);

  const getGalleries = useCallback(
    (f) => dispatch(fetchGalleries(f)),
    [dispatch],
  );
  const getGalleryById = useCallback(
    (id) => dispatch(fetchGalleryById(id)),
    [dispatch],
  );

  const createNewGallery = useCallback(
    (data, createdBy) => dispatch(createGallery({ data, createdBy })).unwrap(),
    [dispatch],
  );

  const updateExistingGallery = useCallback(
    (id, data) => dispatch(updateGallery({ id, data })).unwrap(),
    [dispatch],
  );

  const deleteExistingGallery = useCallback(
    (id) => dispatch(deleteGallery(id)).unwrap(),
    [dispatch],
  );

  const archiveExistingGallery = useCallback(
    (id) => dispatch(archiveGallery(id)).unwrap(),
    [dispatch],
  );

  const publishExistingGallery = useCallback(
    (id) => dispatch(publishGallery(id)).unwrap(),
    [dispatch],
  );

  const unpublishExistingGallery = useCallback(
    (id) => dispatch(unpublishGallery(id)).unwrap(),
    [dispatch],
  );

  const uploadNewImages = useCallback(
    (galleryId, imagesData) =>
      dispatch(uploadGalleryImages({ galleryId, imagesData })).unwrap(),
    [dispatch],
  );

  const deleteExistingImage = useCallback(
    (galleryId, imageUrl) =>
      dispatch(deleteGalleryImage({ galleryId, imageUrl })).unwrap(),
    [dispatch],
  );

  const toggleImageVisibilityFn = useCallback(
    (galleryId, imageUrl) =>
      dispatch(toggleImageVisibility({ galleryId, imageUrl })).unwrap(),
    [dispatch],
  );

  const setCover = useCallback(
    (galleryId, imageUrl, thumbnailUrl) =>
      dispatch(setCoverImage({ galleryId, imageUrl, thumbnailUrl })).unwrap(),
    [dispatch],
  );

  const updateSettings = useCallback(
    (galleryId, settingsUpdates) =>
      dispatch(updateGallerySettings({ galleryId, settingsUpdates })).unwrap(),
    [dispatch],
  );

  const getOverallStats = useCallback(
    () => dispatch(fetchGalleryOverallStats()),
    [dispatch],
  );

  const clearErrorStateFn = useCallback(
    () => dispatch(clearErrorState()),
    [dispatch],
  );
  const clearSuccessStateFn = useCallback(
    () => dispatch(clearSuccessState()),
    [dispatch],
  );
  const updateFiltersFn = useCallback(
    (f) => dispatch(updateFilters(f)),
    [dispatch],
  );
  const clearSelected = useCallback(
    () => dispatch(clearSelectedGallery()),
    [dispatch],
  );

  return {
    galleries,
    selectedGallery,
    stats,
    loading,
    error,
    success,
    filters,
    getGalleries,
    getGalleryById,
    createNewGallery,
    updateExistingGallery,
    deleteExistingGallery,
    archiveExistingGallery,
    publishExistingGallery,
    unpublishExistingGallery,
    uploadNewImages,
    deleteExistingImage,
    toggleImageVisibility: toggleImageVisibilityFn,
    setCoverImage: setCover,
    updateSettings,
    getOverallStats,
    clearErrorState: clearErrorStateFn,
    clearSuccessState: clearSuccessStateFn,
    updateFilters: updateFiltersFn,
    clearSelectedGallery: clearSelected,
  };
};

export default usePublicGallery;
